// サイドバーのナビゲーション定義。freee会計の左メニュー構成に合わせる。
// ホーム（単独リンク）＋ セクション見出し（経理／会計）＋ 折りたたみグループ（>）。
//
// 「会計」配下の4グループ（取引入力・会計帳簿・分析レポート・決算申告）は
// freee実画面を巡回して採取した構成をすべて実リンクとして展開する（枝まで作り込み対象）。
// それ以外のグループは、新規項目は「準備中」スタブへ、上位プラン(🔒)・外部サービス(↗)は無効表示にする。

export type NavLink = {
  href: string;
  label: string;
  /** true の場合クリック不可・グレー表示（🔒上位プラン・↗外部サービスなど、ローカル版の対象外）。 */
  disabled?: boolean;
  /** disabled時の理由バッジ（例: "上位プラン", "外部連携"）。 */
  note?: string;
};

export type NavEntry =
  | { kind: "link"; href: string; label: string; icon: string }
  | { kind: "group"; label: string; icon: string; children: NavLink[] };

export type NavSection = { title?: string; entries: NavEntry[] };

const LOCKED = "上位プラン";
const EXTERNAL = "外部連携";

export const NAV_SECTIONS: NavSection[] = [
  {
    entries: [
      { kind: "link", href: "/", label: "ホーム", icon: "home" },
      { kind: "link", href: "/review", label: "会計レビュー", icon: "report" },
      { kind: "link", href: "/calendar", label: "カレンダー", icon: "grid" },
    ],
  },
  {
    title: "経理",
    entries: [
      {
        kind: "group",
        label: "請求・入金",
        icon: "invoice",
        children: [
          { href: "/quotes", label: "見積書" },
          { href: "/invoices/delivery", label: "納品書" },
          { href: "/invoices", label: "請求書" },
          { href: "/invoices/receipts", label: "領収書" },
          { href: "/invoices/contracts", label: "契約書" },
          { href: "/invoices/projects", label: "案件管理" },
          { href: "/invoices/receivables", label: "入金明細・消込" },
          { href: "/invoices/receivables-report", label: "入金管理レポート" },
        ],
      },
      {
        kind: "group",
        label: "発注・経費・支払",
        icon: "expense",
        children: [
          { href: "/expenses/orders", label: "発注書" },
          { href: "/expenses/payment-notices", label: "支払通知書" },
          { href: "/folder-browser", label: "フォルダブラウザ（ファイルボックス）" },
          { href: "#", label: "freeeカード Unlimited", disabled: true, note: EXTERNAL },
          { href: "#", label: "小口現金出納帳", disabled: true, note: EXTERNAL },
          { href: "#", label: "振込申請", disabled: true, note: EXTERNAL },
          { href: "/expenses", label: "経費精算" },
          { href: "#", label: "支払管理レポート", disabled: true, note: LOCKED },
        ],
      },
    ],
  },
  {
    title: "会計",
    entries: [
      {
        kind: "group",
        label: "取引入力",
        icon: "deals",
        children: [
          { href: "/banking", label: "自動で経理" },
          { href: "/folder-browser", label: "フォルダブラウザ（ファイルボックス）" },
          { href: "/file-routing", label: "証憑ファイルの移動履歴" },
          { href: "/deals", label: "取引を登録（収入・支出）" },
          { href: "/journal/new?mode=journal", label: "仕訳帳形式" },
          { href: "/journal/new?mode=ledger", label: "元帳形式" },
          { href: "/deals/batch", label: "連続取引登録" },
          { href: "/journal/new", label: "振替伝票" },
          { href: "#", label: "freeeカードで経理", disabled: true, note: EXTERNAL },
          { href: "/deals/import", label: "エクセルインポート" },
          { href: "#", label: "給与取引の登録", disabled: true, note: EXTERNAL },
        ],
      },
      {
        kind: "group",
        label: "会計帳簿",
        icon: "journal",
        children: [
          { href: "/reports/bs", label: "貸借対照表" },
          { href: "/reports/pl", label: "損益計算書" },
          { href: "/reports/manufacturing", label: "製造原価報告書" },
          { href: "/reports/trend", label: "月次推移" },
          { href: "/reports/monthly", label: "月次試算表" },
          { href: "/reports/settlement", label: "決算報告書" },
          { href: "/journal", label: "取引と仕訳帳" },
          { href: "/ledger", label: "総勘定元帳" },
          { href: "/assets", label: "固定資産台帳" },
          { href: "/portfolio", label: "ポートフォリオ（有価証券）" },
          { href: "/banking?status=all", label: "明細の一覧" },
          { href: "/journal/review", label: "修正待ちリスト" },
          { href: "/journal/duplicates", label: "重複チェック" },
          { href: "/journal/unattached", label: "ファイル紐付けチェック" },
          { href: "#", label: "日次残高推移", disabled: true, note: LOCKED },
        ],
      },
      {
        kind: "group",
        label: "分析・レポート",
        icon: "report",
        children: [
          { href: "/reports", label: "損益レポート" },
          { href: "/reports/cash", label: "現預金レポート" },
          { href: "/reports/cashflow", label: "資金繰りレポート" },
          { href: "#", label: "経営ダッシュボード", disabled: true, note: LOCKED },
          { href: "#", label: "収益レポート", disabled: true, note: LOCKED },
          { href: "#", label: "費用レポート", disabled: true, note: LOCKED },
          { href: "#", label: "カスタムレポート", disabled: true, note: LOCKED },
        ],
      },
      {
        kind: "group",
        label: "決算申告",
        icon: "closing",
        children: [
          { href: "/assets", label: "固定資産台帳" },
          { href: "/closing/inventory", label: "在庫棚卸" },
          { href: "/closing/tax-summary", label: "消費税区分別表" },
          { href: "/closing/statements", label: "決算書" },
          { href: "#", label: "法人税申告書", disabled: true, note: EXTERNAL },
          { href: "#", label: "消費税申告書", disabled: true, note: EXTERNAL },
          { href: "#", label: "内訳書・概況書", disabled: true, note: EXTERNAL },
          { href: "#", label: "償却資産申告書", disabled: true, note: EXTERNAL },
          { href: "#", label: "支払調書", disabled: true, note: EXTERNAL },
          { href: "/closing/export", label: "連携用データの作成" },
          { href: "/closing", label: "年度締め" },
          { href: "#", label: "連結会計", disabled: true, note: EXTERNAL },
          { href: "#", label: "月締め", disabled: true, note: LOCKED },
        ],
      },
    ],
  },
  {
    entries: [
      {
        kind: "group",
        label: "給与計算",
        icon: "wallet",
        children: [
          { href: "#", label: "従業員", disabled: true, note: EXTERNAL },
          { href: "#", label: "勤怠", disabled: true, note: EXTERNAL },
          { href: "#", label: "給与計算", disabled: true, note: EXTERNAL },
          { href: "#", label: "年末調整", disabled: true, note: EXTERNAL },
          { href: "#", label: "労務アウトソース", disabled: true, note: EXTERNAL },
        ],
      },
      {
        kind: "group",
        label: "会計連携機能",
        icon: "sync",
        children: [{ href: "#", label: "連携アプリを探す", disabled: true, note: EXTERNAL }],
      },
    ],
  },
  {
    entries: [
      {
        kind: "group",
        label: "入力効率化",
        icon: "voucher",
        children: [
          { href: "/settings/auto-rules", label: "自動登録ルール" },
          { href: "/settings/file-rules", label: "ファイルの登録ルール" },
          { href: "/settings/deal-templates", label: "取引テンプレート" },
          { href: "/settings/journal-templates", label: "振替伝票テンプレート" },
          { href: "/mail-hub", label: "メール自動仕分けルール" },
          { href: "#", label: "給与取引の登録ルール", disabled: true, note: EXTERNAL },
        ],
      },
      {
        kind: "group",
        label: "マスタ・口座",
        icon: "accounts",
        children: [
          { href: "/wallets", label: "口座" },
          { href: "/accounts", label: "勘定科目" },
          { href: "/accounts/tax-categories", label: "税区分" },
          { href: "/partners", label: "取引先" },
          { href: "/items", label: "品目" },
          { href: "/sections", label: "部門" },
          { href: "/tags", label: "メモタグ" },
        ],
      },
      { kind: "link", href: "/settings", label: "その他設定", icon: "settings" },
    ],
  },
];

// フラットな全リンク（後方互換・アクティブ判定用）。disabled は除外。
export const NAV_LINKS: NavLink[] = NAV_SECTIONS.flatMap((s) =>
  s.entries.flatMap((e) => (e.kind === "link" ? [{ href: e.href, label: e.label }] : e.children)),
).filter((l) => !l.disabled);
